// PWA + Local Push Notification Helpers
import { supabase } from "@/integrations/supabase/client";

export function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

export function isNotificationEnabled(): boolean {
  return "Notification" in window && Notification.permission === "granted";
}

// Save push subscription to database
export async function savePushSubscription(userId: string, vapidPublicKey?: string): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const pm = (registration as any).pushManager;
    if (!pm) return false;
    const subscription = await pm.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidPublicKey,
    });

    const subJson = subscription.toJSON();
    if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) return false;

    await supabase.from("push_subscriptions").upsert({
      user_id: userId,
      endpoint: subJson.endpoint,
      p256dh: subJson.keys.p256dh,
      auth: subJson.keys.auth,
    } as any, { onConflict: "user_id,endpoint" });

    return true;
  } catch {
    return false;
  }
}

// Schedule local reminder notifications using setTimeout
// (No backend push server needed — works while app tab is open or SW is active)

interface ReminderConfig {
  petName: string;
  mode: string;
}

const REMINDER_MESSAGES = {
  morning: (name: string) => ({
    title: `🍖 ${name} голодный!`,
    body: "Не забудь покормить с утра!",
  }),
  evening: (name: string) => ({
    title: `🍖 Время ужина!`,
    body: `${name} ждёт вечернюю порцию.`,
  }),
  walk: (name: string) => ({
    title: `🦮 Пора гулять!`,
    body: `${name} уже смотрит на дверь.`,
  }),
  checkin: (name: string) => ({
    title: `🐾 ${name} скучает`,
    body: "Загляни — поболтаем!",
  }),
};

let reminderTimers: ReturnType<typeof setTimeout>[] = [];

export function scheduleLocalReminders(config: ReminderConfig) {
  clearReminders();

  if (!isNotificationEnabled()) return;

  const now = new Date();
  const hour = now.getHours();

  // Morning feeding reminder at 8:00
  if (hour < 8) {
    const delay = getDelayUntilHour(8);
    reminderTimers.push(setTimeout(() => showNotification(REMINDER_MESSAGES.morning(config.petName)), delay));
  }

  // Walk reminder at 16:00
  if (hour < 16) {
    const delay = getDelayUntilHour(16);
    reminderTimers.push(setTimeout(() => showNotification(REMINDER_MESSAGES.walk(config.petName)), delay));
  }

  // Evening feeding at 19:00
  if (hour < 19) {
    const delay = getDelayUntilHour(19);
    reminderTimers.push(setTimeout(() => showNotification(REMINDER_MESSAGES.evening(config.petName)), delay));
  }

  // Check-in if no interaction for 4 hours
  reminderTimers.push(setTimeout(() => showNotification(REMINDER_MESSAGES.checkin(config.petName)), 4 * 60 * 60 * 1000));
}

export function clearReminders() {
  reminderTimers.forEach(clearTimeout);
  reminderTimers = [];
}

function getDelayUntilHour(targetHour: number): number {
  const now = new Date();
  const target = new Date(now);
  target.setHours(targetHour, 0, 0, 0);
  if (target <= now) target.setDate(target.getDate() + 1);
  return target.getTime() - now.getTime();
}

function showNotification(msg: { title: string; body: string }) {
  if (!isNotificationEnabled()) return;

  // Use service worker notification if available
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((reg) => {
      reg.showNotification(msg.title, {
        body: msg.body,
        icon: "/pwa-192.png",
        badge: "/favicon.ico",
        tag: "peternity-reminder",
      } as NotificationOptions);
    });
  } else {
    // Fallback to Notification API
    new Notification(msg.title, { body: msg.body, icon: "/pwa-192.png" });
  }
}
